import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";

/**
 * Scroll-reveal hook. Returns a `ref` to attach to the target element.
 *
 * Design goals:
 * - **No-JS / hydration safe:** the element renders visible; this hook only
 *   *adds* `data-reveal` on the client to opt into the hidden pre-animation
 *   state, so a visitor without JS (or a failed hydration) always sees content.
 * - **Maximally functional:** all reveal elements on the page share ONE
 *   `IntersectionObserver` (keyed by element via a Map) instead of one per
 *   instance, and state is driven imperatively through the ref — the hook never
 *   triggers a React re-render.
 * - Respects `prefers-reduced-motion`: reduced-motion elements are left visible
 *   and never observed.
 */

const revealCallbacks = new Map();
let revealObserver = null;
function getRevealObserver() {
  if (revealObserver) return revealObserver;
  revealObserver = new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const callback = revealCallbacks.get(entry.target);
      if (callback) {
        callback();
        revealCallbacks.delete(entry.target);
        revealObserver?.unobserve(entry.target);
      }
    }
  }, {
    threshold: 0.12,
    rootMargin: "0px 0px -8% 0px"
  });
  return revealObserver;
}
export function useReveal() {
  const $ = _c(3);
  const ref = useRef(null);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const element = ref.current;
      if (!element) {
        return;
      }
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        return;
      }
      element.setAttribute("data-reveal", "");
      const observer = getRevealObserver();
      revealCallbacks.set(element, () => element.setAttribute("data-visible", ""));
      let innerFrame = 0;
      const outerFrame = requestAnimationFrame(() => {
        innerFrame = requestAnimationFrame(() => observer.observe(element));
      });
      return () => {
        cancelAnimationFrame(outerFrame);
        cancelAnimationFrame(innerFrame);
        revealCallbacks.delete(element);
        observer.unobserve(element);
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      ref
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}